import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import { type SupportZendeskTicketModel, type UserModel } from "@doitintl/cmp-models";

import useAssigneeUsers from "../hooks/useAssigneeUsers";
import { useListZendeskTickets } from "../hooks/useListZendeskTickets";

type ZendeskTicketsContextType = {
  zendeskTickets: SupportZendeskTicketModel[] | undefined;
  loading: boolean;
  error: Error | undefined;
  assigneeUsers: Map<string, UserModel> | undefined;
  assigneeUsersLoading: boolean;
};

const ZendeskTicketsContext = createContext<ZendeskTicketsContextType | undefined>(undefined);

export function ZendeskTicketsContextProvider({ children }: { children: ReactNode }) {
  const [tickets, loading, error] = useListZendeskTickets();
  const [isSettled, setIsSettled] = useState(false);
  const timeoutRef = useRef<number>();

  const filteredTickets = useMemo(
    () => (tickets ? tickets.filter((t) => !t.ticket.description.includes("created a new chat called")) : undefined),
    [tickets]
  );

  const { assigneeUsers, assigneeUsersLoading } = useAssigneeUsers(filteredTickets);

  useEffect(() => {
    if (loading) {
      setIsSettled(false);
    } else {
      timeoutRef.current = window.setTimeout(() => {
        setIsSettled(true);
      }, 0);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loading]);

  const contextValue = useMemo(
    () => ({
      zendeskTickets: filteredTickets,
      loading: loading || !isSettled,
      error,
      assigneeUsers,
      assigneeUsersLoading,
    }),
    [filteredTickets, loading, isSettled, error, assigneeUsers, assigneeUsersLoading]
  );

  return <ZendeskTicketsContext.Provider value={contextValue}>{children}</ZendeskTicketsContext.Provider>;
}

export function useZendeskTicketsContext() {
  const context = useContext(ZendeskTicketsContext);
  if (context === undefined) {
    throw new Error("useZendeskTickets must be used within a ZendeskTicketsProvider");
  }
  return context;
}
